import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CostService } from 'src/app/services/cost.service';
import { CostUnitOfMeasureModel } from 'src/app/models/season-models/cost-unit-of-measure-model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-bulk-cost-manager',
    templateUrl: './bulk-cost-manager.component.html',
    styleUrls: ['./bulk-cost-manager.component.css'],
    standalone: false
})
export class BulkCostManagerComponent implements OnInit {

  unitsOfMeasure: CostUnitOfMeasureModel[] = [];
  fixedCostValue: number = 0;
  costInputs: any[] = [];
  isLoading: boolean = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { selectedFields: any[] },
    public dialogRef: MatDialogRef<BulkCostManagerComponent>,
    private costService: CostService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.loadUnitsOfMeasure();
    this.loadInputOptions();
  }

  loadUnitsOfMeasure(): void {
    this.costService.getUnitsOfMeasure().subscribe(uoms => {
      this.unitsOfMeasure = uoms;
    });
  }

  loadInputOptions(): void {
    const detailIds = this.data.selectedFields.map(f => f.detailsId || f.id);
    const uniqueInputs = new Map<string, any>();

    let completed = 0;
    detailIds.forEach(detailId => {
      this.costService.getInputOptions(detailId).subscribe(options => {
        (options || []).forEach(opt => {
          const key = opt.inputName + '|' + opt.sourceType;
          if (!uniqueInputs.has(key)) {
            uniqueInputs.set(key, { type: opt.inputName, sourceType: opt.sourceType, costValue: 0, unitOfMeasureId: null });
          }
        });
        completed++;
        if (completed === detailIds.length) {
          this.costInputs = [...uniqueInputs.values()];
          this.isLoading = false;
        }
      }, () => {
        completed++;
        if (completed === detailIds.length) {
          this.costInputs = [...uniqueInputs.values()];
          this.isLoading = false;
        }
      });
    });

    if (detailIds.length === 0) {
      this.isLoading = false;
    }
  }

  apply(): void {
    const detailIds = this.data.selectedFields.map(f => f.detailsId || f.id);
    const requests = [];

    detailIds.forEach(detailId => {
      // Fixed cost (only if > 0)
      if (this.fixedCostValue > 0) {
        requests.push(this.costService.upsertCost({
          seasonFieldDetailId: detailId,
          mapType: 'FIXED',
          mapCategory: 'FIXED',
          costValue: this.fixedCostValue,
          unitOfMeasureId: null
        }));
      }

      // VRT / As Applied costs (only if > 0)
      this.costInputs.forEach(input => {
        if (input.costValue > 0 && input.unitOfMeasureId) {
          requests.push(this.costService.upsertCost({
            seasonFieldDetailId: detailId,
            mapType: input.type,
            mapCategory: input.sourceType,
            costValue: input.costValue,
            unitOfMeasureId: input.unitOfMeasureId
          }));
        }
      });
    });

    if (requests.length === 0) {
      this.snackBar.open('No cost values to apply.', 'Dismiss', { duration: 3000 });
      return;
    }

    forkJoin(requests).subscribe(() => {
      this.snackBar.open('Costs applied to ' + detailIds.length + ' fields.', 'Dismiss', { duration: 5000 });
      this.dialogRef.close({ applied: true });
    }, () => {
      this.snackBar.open('Error applying costs.', 'Dismiss', { duration: 5000 });
    });
  }

  cancel(): void {
    this.dialogRef.close({ applied: false });
  }
}
